import React from 'react';
import KI from '../theme/palette';

// Self-contained SVG grouped bar chart: Achats vs Ventes per week — no external
// charting dep. Matches SalesTrendChart's look: a dark beveled navy panel, a
// montant (Y) + semaines (X) échelle, and gradient-filled glowing bars.
// Rendered static — no animations.
//
// Expected `data` shape (from the dashboard API `weekly_compare`):
//   [{ week: 'YYYY-MM-DD', achat: number, vente: number }]
// Default hues — sky for achats everywhere (owner rule: no purple). Pages can
// still pass `achatColor` to repaint the achats series (green + sky passes CVD).
const DEFAULT_VENTE = KI.spruce; // Ventes #15803D (Brand Kit §05, 2026-09-04)
const DEFAULT_ACHAT = KI.sapphire; // Achats #2563EB (Brand Kit §05)

// viewBox units — same canvas as SalesTrendChart so both cards line up visually.
const W = 1000;
const H = 460;
const M = { top: 22, right: 22, bottom: 46, left: 70 }; // margins hold the échelle
const PLOT = { x: M.left, y: M.top, w: W - M.left - M.right, h: H - M.top - M.bottom };

// Compact amount for the Y échelle (3-decimal DT would be unreadable here).
const fmtTick = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(a % 1_000_000 ? 1 : 0)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(a % 1_000 ? 1 : 0)}k`;
  return `${Math.round(v)}`;
};

// X échelle label — depends on the bucket granularity sent by the API:
// day/week → DD/MM (Monday for weeks), month → « août 26 ».
const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const fmtBucket = (iso, granularity) => {
  const p = String(iso || '').split('-');
  if (p.length !== 3) return String(iso || '');
  if (granularity === 'month') return `${MONTHS_FR[Number(p[1]) - 1] || p[1]} ${p[0].slice(2)}`;
  return `${p[2]}/${p[1]}`;
};

// "Nice" rounded number for human-friendly axis ticks.
const niceNum = (range, round) => {
  const exp = Math.floor(Math.log10(range || 1));
  const f = (range || 1) / 10 ** exp;
  let nf;
  if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * 10 ** exp;
};

// Rounded-top bar (flat bottom) as a path so the top corners are softened
// (Composants-2 §12 : coins hauts 3px).
const barPath = (bx, by, bw, bh, radius = 8) => {
  const r = Math.min(bw / 2, bh, radius);
  if (bh <= 0) return '';
  return `M ${bx},${(by + bh).toFixed(1)} L ${bx},${(by + r).toFixed(1)} `
    + `Q ${bx},${by.toFixed(1)} ${(bx + r).toFixed(1)},${by.toFixed(1)} `
    + `L ${(bx + bw - r).toFixed(1)},${by.toFixed(1)} `
    + `Q ${(bx + bw).toFixed(1)},${by.toFixed(1)} ${(bx + bw).toFixed(1)},${(by + r).toFixed(1)} `
    + `L ${(bx + bw).toFixed(1)},${(by + bh).toFixed(1)} Z`;
};

const PurchaseSalesBarChart = ({ data: rawData, transparent = false, light = false,
                                 venteColor = DEFAULT_VENTE, achatColor = DEFAULT_ACHAT,
                                 granularity = 'week', legend = 'top' }) => {
  const SERIES = [
    { key: 'vente', label: 'Ventes', color: venteColor },
    { key: 'achat', label: 'Achats', color: achatColor },
  ];
  const data = Array.isArray(rawData) ? rawData : [];

  if (data.length < 1) {
    return (
      <div className={`flex flex-col items-center justify-center h-72 rounded-2xl ${
        light ? 'bg-gray-50 text-gray-400' : 'text-gray-400'
      }`} style={light ? undefined : { background: KI.darkBg }}>
        <p className="text-sm">Pas assez de données pour comparer achats et ventes</p>
      </div>
    );
  }

  // `light` : pas de boîtier sombre — barres en aplat sans halo, grille
  // pointillée #F3F4F6, échelles 10.5px #9CA3AF (Composants-2 §12).
  const gridStroke = light ? KI.ramps.gray[100] : KI.pureWhite;
  const gridOp = (t) => (t === 0 ? (light ? 1 : 0.32) : (light ? 1 : 0.14));
  const gridDash = '4 6';
  const tickFill = light ? KI.faint : KI.ramps.gray[400];
  const tickSize = 15;
  const flat = transparent || light;

  // Shared "nice" scale across both series, floored at 0.
  const vals = data.flatMap((d) => SERIES.map((s) => Number(d[s.key]) || 0));
  const rawMax = Math.max(1, ...vals);
  const step = niceNum(niceNum(rawMax, false) / 4, true) || 1;
  const yMin = 0;
  const yMax = Math.ceil(rawMax / step) * step;
  const ticks = [];
  for (let v = yMin; v <= yMax + step / 2; v += step) ticks.push(v);

  const y = (v) => PLOT.y + PLOT.h * (1 - (v - yMin) / (yMax - yMin || 1));
  const baseY = y(0);

  // Group geometry: one group per bucket (jour/semaine/mois), two bars per group.
  const groupW = PLOT.w / data.length;
  const groupPad = groupW * 0.16;   // empty space on each side of a group
  const barGap = groupW * 0.06;     // gap between the two bars of a group
  const barW = Math.max(4, (groupW - groupPad * 2 - barGap) / SERIES.length);

  // Lisibilité quand la période donne beaucoup de seaux : au-delà de 16
  // groupes les valeurs par barre se chevauchent — on les masque (le survol
  // du détail reste dans la légende/le tableau), et on saute des labels X
  // pour garder une échelle aérée.
  const showValues = data.length <= 16;
  const xLabelEvery = Math.max(1, Math.ceil(data.length / 13));

  // Légende Ventes · Achats — puce + dernière valeur de chaque série.
  // `legend="top"` (défaut, Brand Kit §05) la garde au-dessus du graphe ;
  // `legend="bottom"` reprend le placement de la PROD : SOUS le graphe et
  // CENTRÉ (`mt-3 justify-center`). Demandé par le propriétaire (2026-09-07)
  // pour la case « Achats / jour » du tableau de bord.
  const legendRow = (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-1 ${legend === 'bottom' ? 'mt-3 justify-center' : 'mb-3'}`}>
      {SERIES.map((s) => {
        const last = Number(data[data.length - 1]?.[s.key]) || 0;
        return (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-3 h-2 rounded-sm" style={{ background: s.color }} />
            {s.label}
            <span className="font-semibold text-gray-900 tabular-nums">{fmtTick(last)}</span>
          </span>
        );
      })}
    </div>
  );

  return (
    <div>
      {legend !== 'bottom' && legendRow}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Comparaison des achats et des ventes sur la période filtrée, avec échelle montant et temps"
      >
        <defs>
          {/* Beveled navy panel + top sheen for depth — same as the courbe */}
          <linearGradient id="psbc-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={KI.darkCard} />
            <stop offset="1" stopColor={KI.black} />
          </linearGradient>
          <radialGradient id="psbc-sheen" cx="0.5" cy="0" r="0.95">
            <stop offset="0" stopColor={KI.pureWhite} stopOpacity="0.12" />
            <stop offset="1" stopColor={KI.pureWhite} stopOpacity="0" />
          </radialGradient>
          {SERIES.map((s) => (
            <linearGradient key={s.key} id={`psbc-fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={s.color} stopOpacity="1" />
              <stop offset="1" stopColor={s.color} stopOpacity="0.45" />
            </linearGradient>
          ))}
          {SERIES.map((s) => (
            <filter key={s.key} id={`psbc-glow-${s.key}`} x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={s.color} floodOpacity="0.85" />
            </filter>
          ))}
          <clipPath id="psbc-clip">
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} />
          </clipPath>
        </defs>

        {/* The "case": dark beveled panel — skipped when `transparent` (mobile)
            or `light` (white cards). */}
        {!transparent && !light && (
          <>
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} fill="url(#psbc-bg)" />
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} fill="url(#psbc-sheen)" />
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h}
              fill="none" stroke={KI.pureWhite} strokeOpacity="0.10" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
            <line x1={PLOT.x + 14} y1={PLOT.y + 1} x2={PLOT.x + PLOT.w - 14} y2={PLOT.y + 1}
              stroke={KI.pureWhite} strokeOpacity="0.18" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          </>
        )}

        {/* Y échelle (montant) — grid lines + left labels */}
        {ticks.map((t, i) => (
          <g key={`t${i}`}>
            <line x1={PLOT.x} y1={y(t)} x2={PLOT.x + PLOT.w} y2={y(t)}
              stroke={gridStroke} strokeOpacity={gridOp(t)}
              strokeDasharray={t === 0 ? '0' : gridDash}
              vectorEffect="non-scaling-stroke" />
            <text x={PLOT.x - 12} y={y(t) + 4} textAnchor="end"
              fontSize={tickSize} fill={tickFill}>{fmtTick(t)}</text>
          </g>
        ))}

        {/* Bars (clipped to the panel) + per-bar value on top */}
        <g clipPath="url(#psbc-clip)">
          {data.map((d, gi) => {
            const gx = PLOT.x + gi * groupW + groupPad;
            return SERIES.map((s, si) => {
              const v = Number(d[s.key]) || 0;
              const bx = gx + si * (barW + barGap);
              const top = y(v);
              const bh = Math.max(0, baseY - top);
              return (
                <path
                  key={`${gi}-${s.key}`}
                  d={barPath(bx, top, barW, bh, 8)}
                  fill={light ? s.color : `url(#psbc-fill-${s.key})`}
                  filter={flat ? undefined : `url(#psbc-glow-${s.key})`}
                />
              );
            });
          })}
        </g>

        {/* Value labels: above the bar when there's room inside the panel, otherwise
            INSIDE the bar top (white) so a tall bar never writes outside the panel. */}
        {showValues && data.map((d, gi) => {
          const gx = PLOT.x + gi * groupW + groupPad;
          return SERIES.map((s, si) => {
            const v = Number(d[s.key]) || 0;
            if (v <= 0) return null;
            const bx = gx + si * (barW + barGap);
            const top = y(v);
            const roomAbove = top - 7 >= PLOT.y + 14;   // fits above the bar, inside the panel
            const ly = roomAbove ? top - 7 : top + 18;
            const fill = roomAbove ? s.color : KI.pureWhite;
            return (
              <text key={`v${gi}-${s.key}`}
                x={bx + barW / 2} y={ly} textAnchor="middle"
                fontSize="13" fontWeight="700" fill={fill}>{fmtTick(v)}</text>
            );
          });
        })}

        {/* X échelle (jours / semaines / mois) under the black panel */}
        {data.map((d, gi) => (
          gi % xLabelEvery === 0 && (
            <text key={`w${gi}`} x={PLOT.x + gi * groupW + groupW / 2} y={H - M.bottom + 26}
              textAnchor="middle" fontSize={tickSize} fill={tickFill}>{fmtBucket(d.week, granularity)}</text>
          )
        ))}
      </svg>
      {legend === 'bottom' && legendRow}
    </div>
  );
};

export default PurchaseSalesBarChart;
